import { useEffect, useState } from "react";
import {
	Box,
	CircularProgress,
	Divider,
	Grid2,
	IconButton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import type { MonthlyEstimation } from "../models/MonthlyEstimation";

const sectionHeaderSx = {
	color: "primary.main",
	fontWeight: 600,
	fontSize: "0.85rem",
	mt: 0.5,
	mb: 0.25,
};

const readOnlyFieldProps = {
	size: "small" as const,
	fullWidth: true,
	disabled: true,
	slotProps: { inputLabel: { shrink: true } },
};

const fmtDateTime = (raw: string | null | undefined): string => {
	if (!raw) return "";
	return dxtToLocalServerTime(raw, "yyyy-MM-dd HH:mm:ss");
};

const toNumberOrNull = (raw: string): number | null => {
	const trimmed = raw.trim();
	if (trimmed === "") return null;
	const n = Number(trimmed);
	return Number.isFinite(n) ? n : null;
};

interface Props {
	row: MonthlyEstimation | null;
	isSaving: boolean;
	onSave: (forecast: number | null, actual: number | null) => void;
	onClose: () => void;
}

export const ForecastDetailViewer = ({
	row,
	isSaving,
	onSave,
	onClose,
}: Props) => {
	const [forecastInput, setForecastInput] = useState<string>("");
	const [actualInput, setActualInput] = useState<string>("");

	useEffect(() => {
		setForecastInput(
			row?.ForecastedProduction == null ? "" : String(row.ForecastedProduction),
		);
		setActualInput(
			row?.ActualProduction == null ? "" : String(row.ActualProduction),
		);
	}, [row]);

	if (!row) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
					p: 1.5,
				}}
			>
				<Typography variant="body2">Select a month to view details</Typography>
			</Box>
		);
	}

	const handleBlurSave = (field: "Forecasted" | "Actual", value: string) => {
		const parsed = toNumberOrNull(value);
		const original =
			field === "Forecasted" ? row.ForecastedProduction : row.ActualProduction;
		const normalizedOriginal = original ?? null;
		if (normalizedOriginal === parsed) return;

		const forecast =
			field === "Forecasted" ? parsed : toNumberOrNull(forecastInput);
		const actual = field === "Actual" ? parsed : toNumberOrNull(actualInput);
		onSave(forecast, actual);
	};

	return (
		<Box sx={{ px: 1, py: 0.75, height: "100%", overflow: "auto" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 0.25,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{row.Name} {row.Year}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<Tooltip title="Close">
					<IconButton size="small" onClick={onClose} disabled={isSaving}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

			<Divider sx={{ mb: 0.5 }} />

			<Grid2 container spacing={0.75}>
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Production data</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Estimated (tons)"
						type="number"
						value={forecastInput}
						onChange={(e) => setForecastInput(e.target.value)}
						onBlur={(e) => handleBlurSave("Forecasted", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.01 },
						}}
						disabled={isSaving}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Actual (tons)"
						type="number"
						value={actualInput}
						onChange={(e) => setActualInput(e.target.value)}
						onBlur={(e) => handleBlurSave("Actual", e.target.value)}
						fullWidth
						size="small"
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { step: 0.01 },
						}}
						disabled={isSaving || row.Forecast === 1}
						helperText={
							row.Forecast === 1 ? "Future month — no actual yet" : undefined
						}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Audit</Typography>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Last change"
						value={fmtDateTime(row.LastChange)}
						{...readOnlyFieldProps}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="User"
						value={row.User ?? ""}
						{...readOnlyFieldProps}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
